/**
 * Manual Products Block Handler
 * 
 * Handles checkbox selection and adding multiple products to cart
 */

class ManualProductsBlock {
  constructor(container) {
    this.container = container;
    this.checkboxes = container.querySelectorAll('.manual-product-item__checkbox');
    this.sectionId = container.dataset.sectionId;
    
    this.init();
  }

  init() {
    // Add event listeners to checkboxes (for visual feedback if needed)
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        // Optional: Add visual feedback when items are selected
      });
    });
  }

  getSelectedProducts() {
    const selected = [];
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked && !checkbox.disabled) {
        selected.push({
          productId: checkbox.dataset.productId,
          variantId: checkbox.dataset.variantId
        });
      }
    });
    return selected;
  }

  async addSelectedToCart() {
    const selectedProducts = this.getSelectedProducts();
    
    if (selectedProducts.length === 0) {
      return { success: true, added: 0 };
    }

    try {
      // Add products to cart sequentially
      const results = [];
      for (const product of selectedProducts) {
        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: product.variantId,
              quantity: 1
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.description || 'Failed to add product to cart');
          }

          results.push({ success: true, productId: product.productId });
        } catch (error) {
          console.error('Error adding product to cart:', error);
          results.push({ success: false, productId: product.productId, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const allSuccess = results.every(r => r.success);

      if (allSuccess) {
        this.clearSelections();
      }

      return {
        success: allSuccess,
        added: successCount,
        total: selectedProducts.length,
        results: results
      };
    } catch (error) {
      console.error('Error adding products to cart:', error);
      return { success: false, added: 0, error: error.message };
    }
  }

  clearSelections() {
    this.checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
  }

}

// Store instances globally so product form can access them
window.ManualProductsBlocks = window.ManualProductsBlocks || {};

// Initialize all manual products blocks on page load
document.addEventListener('DOMContentLoaded', () => {
  const blocks = document.querySelectorAll('[data-manual-products-block]');
  blocks.forEach(block => {
    const instance = new ManualProductsBlock(block);
    const sectionId = block.dataset.sectionId;
    if (sectionId) {
      window.ManualProductsBlocks[sectionId] = instance;
    }
  });
});

// Track pending additions per section to avoid duplicates
const pendingAdditions = new Set();

// Listen for cart update events and add selected products after main product is added
document.addEventListener('cart:updated', async (e) => {
  // Only process if this is from the product section (main product was just added)
  if (!e.detail || e.detail.source !== 'product-section') {
    return;
  }

  // Find the product section that triggered this
  const productSections = document.querySelectorAll('[data-product-id]');
  productSections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    if (!sectionId || pendingAdditions.has(sectionId)) {
      return;
    }

    // Get the manual products block for this section
    const manualProductsBlock = window.ManualProductsBlocks[sectionId];
    if (!manualProductsBlock) return;

    // Get selected products
    const selectedProducts = manualProductsBlock.getSelectedProducts();
    if (selectedProducts.length === 0) {
      return; // No products selected
    }

    // Mark as pending to avoid duplicate additions
    pendingAdditions.add(sectionId);

    // Small delay to ensure main product is fully added
    setTimeout(async () => {
      const result = await manualProductsBlock.addSelectedToCart();
      
      if (result.added > 0) {
        // Refresh cart to update counts
        try {
          const cartResponse = await fetch('/cart.js', {
            headers: { 'Accept': 'application/json' }
          });
          
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            
            // Dispatch cart update event
            if (window.publish) {
              window.publish('cart-update', {
                source: 'manual-products',
                cartData: cart
              });
            }
            
            document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { 
                source: 'manual-products',
                itemCount: cart.item_count,
                cartData: cart
              }
            }));
          }
        } catch (error) {
          console.error('Error refreshing cart:', error);
        }
      }
      
      // Remove from pending after a delay to allow for retries
      setTimeout(() => {
        pendingAdditions.delete(sectionId);
      }, 2000);
    }, 300);
  });
});
